import { useMemo } from 'react';
import { useDepartments } from '@/api/hooks';
import type { WireDepartment } from '@/api/wire';

/**
 * What a department is called, and whether it only reads — as the platform
 * says, not as this interface remembers (outreach audit A-9).
 *
 * `platform.department` holds both: `display_name` from the ledger's
 * `index.yaml`, and `read_only` from its `access: read_only`, which the
 * grant trigger and the gateway enforce. `departments/list` answers every
 * caller with the listing, so one query serves every screen. Until it has
 * answered, a name reads as itself, prettified — never blank, and never a
 * guess at "read only", which here is a hint and nowhere a control.
 */
export interface DepartmentNames {
  label: (name: string) => string;
  readOnly: (name: string) => boolean;
  /** Every department the platform has, in its order; null until known. */
  names: string[] | null;
  /** The whole listing row — lead, status, agents — or null until known. */
  info: (name: string) => WireDepartment | null;
}

export function prettify(name: string): string {
  return name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function useDepartmentNames(enabled = true): DepartmentNames {
  // A department is renamed by a ledger change and a sync, not by the
  // minute; five minutes keeps every screen off the network for it.
  const q = useDepartments({ staleTime: 5 * 60_000, enabled });
  return useMemo(() => {
    const byName = new Map((q.data?.departments ?? []).map((d) => [d.name, d]));
    return {
      label: (name) => byName.get(name)?.displayName ?? prettify(name),
      readOnly: (name) => byName.get(name)?.readOnly === true,
      names: q.data?.departments ? q.data.departments.map((d) => d.name) : null,
      info: (name) => byName.get(name) ?? null,
    };
  }, [q.data]);
}

/** The one most screens need: a department's name in words. */
export function useDepartmentLabel(): (name: string) => string {
  return useDepartmentNames().label;
}
